/** Plan My Week carousel mode: the plan rendered as client cards beside a live map.
 *
 *  The slide-out covered the map, which is where the route is. Here the carousel
 *  holds the plan and the map stays visible, so a rep can see a stop and where it
 *  is at the same time.
 *
 *  Structure follows the RDS inspection call list: collapsible sections with a
 *  left accent bar and a count pill, rather than sticky dividers.
 */
import React, { useCallback, useMemo, useState } from 'react';
import './SalesPlanCards.css';
import { TIER_COLOR } from './salesViewTokens';
import { SalesStatusControl } from './SalesStatusControl';
import { SalesforceLink } from '../shared/SalesforceLink';
import { groupByTrip, formatMiles, formatDriveLeg, formatLeg, formatTripDrive, formatLocation, tripColor, changeLabel, googleTripUrls, navStopUrl } from './salesPlanFormat';
import type { NavApp, NavPref } from '../../maps/navPreference';
import { TakeMeThere } from './TakeMeThere';
import { SalesPlanAddNearby } from './SalesPlanAddNearby';
import type { DisplayStop } from './salesPlanFormat';
import type { PlanEdit, PlanNearbyClient, PlannedStop, SalesTier, StartAddress, TripDrive } from '../../api/types';
import { track } from '../../telemetry/usage';

export interface SalesPlanCardsProps {
  /** Already merged: the recommendation and the rep's overrides in ONE list. */
  stops: DisplayStop[];
  loading?: boolean;
  /** Which client the board has selected, so card and pin stay in step. */
  selectedClientId?: string | null;
  busyClientId?: string | null;
  onSelect?: (clientId: string) => void;
  onHover?: (clientId: string | null) => void;
  onEdit?: (edit: Omit<PlanEdit, 'source'>) => void;
  /** Who else the rep could see near a trip. Absent, the add control is not offered:
   *  a button that cannot answer is worse than no button. */
  onFindNearby?: (tripNo: number | null) => Promise<PlanNearbyClient[]>;
  /** Close-out, same control the board uses. */
  onStatusChange?: (clientId: string, status: string, callbackDate?: string) => void;
  /** Fix an account as always a call / visit, or clear it (null). Absent, not offered. */
  onSetAccountMode?: (clientId: string, mode: 'VISIT' | 'CALL' | null) => void;
  /** Each trip's priced drive (CSE-20). Absent on an unpriced plan: planner miles only. */
  trips?: TripDrive[];
  /** What every drive estimate is based on, shown once under the list. */
  driveBasis?: string;
  /** The rep's saved starting points, offered in each trip's start picker. */
  startAddresses?: StartAddress[];
  /** Choose where a trip starts. Absent, the start is shown but not offered. */
  onSetTripStart?: (tripNo: number, start: string) => void;
  /** This device's navigation preference for "Take Me There" (default: the phone decides). */
  navApp?: NavPref;
  /** Remember an app the rep picked from Take Me There's menu (iPhone). */
  onChooseNavApp?: (app: NavApp) => void;
}

/** Confirmed and reported stay APART. A tick for both would tell a rep that work
 *  Salesforce has not seen is corroborated, which is the claim that gets them
 *  corrected in public. */
/** The rep's own words from Salesforce, where there are any. UNTRUSTED FREE TEXT:
 *  rendered as a quoted string and never as markup. Shown INSTEAD of the reasons --
 *  once work has happened, why we suggested it stops being the interesting part. */
const noteText = (stop: PlannedStop): string | null => {
  const first = stop.completion?.notes?.[0] as { subject?: unknown } | undefined;
  return typeof first?.subject === 'string' && first.subject ? first.subject : null;
};

const COMPLETION_MARK: Record<string, { glyph: string; label: string; cls: string }> = {
  confirmed: { glyph: '✓', label: 'Worked — confirmed in Salesforce', cls: 'spc-done' },
  reported: { glyph: '◐', label: 'You closed this out; Salesforce has not caught up', cls: 'spc-reported' },
};

export const SalesPlanCards: React.FC<SalesPlanCardsProps> = ({
  stops,
  loading,
  selectedClientId,
  busyClientId,
  onSelect,
  onHover,
  onEdit,
  onFindNearby,
  onStatusChange,
  onSetAccountMode,
  trips,
  driveBasis,
  startAddresses,
  onSetTripStart,
  navApp,
  onChooseNavApp,
}) => {
  const groups = useMemo(() => groupByTrip(stops), [stops]);
  const tripByNo = useMemo(
    () => new Map((trips ?? []).map((t) => [t.trip_no, t])),
    [trips],
  );
  const anyStraight = (trips ?? []).some((t) => t.estimate === 'straight_line' || t.estimate === 'mixed');
  const anyDrive = (trips ?? []).some((t) => t.estimate === 'drive' || t.estimate === 'mixed');
  /* Every section shut until asked. The headers still carry the count, the miles and
   * done/total, so a collapsed list is a summary rather than an empty screen. */
  const [collapsed, setCollapsed] = useState<Set<string> | null>(null);
  const shutSet = useMemo(
    () => collapsed ?? new Set(groups.map((g) => String(g.tripNo ?? 'calls'))),
    [collapsed, groups],
  );

  const toggle = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev ?? shutSet);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, [shutSet]);

  const expandAll = useCallback(() => setCollapsed(new Set()), []);
  const collapseAll = useCallback(
    () => setCollapsed(new Set(groups.map((g) => String(g.tripNo ?? 'calls')))),
    [groups],
  );

  if (loading && !stops.length) {
    return <div className="spc-empty">Loading your week…</div>;
  }
  if (!stops.length) {
    return <div className="spc-empty">No stops planned for this week.</div>;
  }

  return (
    <div className="spc-root">
      <div className="spc-toolbar">
        <button className="spc-link" onClick={expandAll}>Expand all</button>
        <span className="spc-sep">·</span>
        <button className="spc-link" onClick={collapseAll}>Collapse all</button>
      </div>

      {anyStraight ? (
        <div className="spc-straight-banner" role="note">
          Drive times are unavailable for some legs. Those show straight-line distance, as the
          crow flies, so the real drive is longer.
        </div>
      ) : null}

      {groups.map((group) => {
        const key = String(group.tripNo ?? 'calls');
        const isCalls = group.tripNo === null;
        const shut = shutSet.has(key);
        const trip = isCalls ? undefined : tripByNo.get(group.tripNo!);
        const tripDrive = trip ? formatTripDrive(trip) : null;
        const done = group.stops.filter(
          (s) => s.completion?.evidence === 'confirmed' || s.completion?.evidence === 'reported',
        ).length;
        // Calls have no drive, so they get a neutral accent rather than a route colour
        // that would imply they are on one.
        const accent = isCalls ? 'var(--color-text-secondary, #6b7280)' : tripColor(group.tripNo!);

        return (
          <section className="spc-section" key={key} style={{ ['--spc-accent' as string]: accent }}>
            <button
              className="spc-section-head"
              onClick={() => toggle(key)}
              aria-expanded={!shut}
            >
              <span className="spc-caret">{shut ? '▸' : '▾'}</span>
              <span className="spc-section-name">{isCalls ? 'Calls' : `Trip ${group.tripNo}`}</span>
              <span className="spc-count">{done ? `${done}/${group.stops.length}` : group.stops.length}</span>
              {!isCalls ? (
                tripDrive ? (
                  <span
                    className={`spc-miles${tripDrive.kind === 'straight' ? ' spc-straight' : ''}`}
                    title={tripDrive.kind === 'drive' ? driveBasis : 'Straight-line distance: drive time is unavailable.'}
                  >
                    {tripDrive.text}
                  </span>
                ) : <span className="spc-miles">{formatMiles(group.miles)}</span>
              ) : null}
            </button>

            {!shut ? (
              <div className="spc-section-body">
                {trip ? (
                  <div className="spc-trip-start">
                    <label>
                      Starts from{' '}
                      {onSetTripStart ? (
                        <select
                          aria-label={`Where trip ${trip.trip_no} starts`}
                          value={trip.start.choice}
                          disabled={!!busyClientId}
                          onChange={(e) => onSetTripStart(trip.trip_no, e.target.value)}
                        >
                          <option value="anchor">the anchor client</option>
                          {trip.trip_no > 1 ? (
                            <option value="previous">the previous trip&rsquo;s last stop</option>
                          ) : null}
                          {(startAddresses ?? []).map((a) => (
                            <option key={a.id} value={`address:${a.id}`}>{a.label}</option>
                          ))}
                        </select>
                      ) : <strong>{trip.start.label}</strong>}
                    </label>
                    {trip.start_unavailable ? (
                      <span className="spc-hint">
                        Your chosen start is not available for this trip, so it starts at the anchor.
                      </span>
                    ) : null}
                    {trip.return_leg ? (
                      <span className={trip.return_leg.source === 'drive' ? 'spc-hint' : 'spc-hint spc-straight'}>
                        Back to {trip.start.label}: {formatDriveLeg(trip.return_leg).text}
                      </span>
                    ) : null}
                  </div>
                ) : null}
                {!isCalls ? (() => {
                  const route = group.stops.filter((s) => s.change !== 'dropped');
                  const urls = googleTripUrls(route);
                  return urls.length ? (
                    <div className="spc-maps">
                      {urls.map((u, i) => (
                        <a key={u} href={u} target="_blank" rel="noopener noreferrer"
                          title="Every stop on this trip, in order"
                          onClick={() => track('sales_view', 'nav', 'trip_link', {},
                            { trip_no: group.tripNo, stops: route.length })}>
                          Google Maps{urls.length > 1 ? ` (${i + 1}/${urls.length})` : ''}
                        </a>
                      ))}
                    </div>
                  ) : null;
                })() : null}
                {group.stops.map((stop, index) => {
                  const isDropped = stop.change === 'dropped';
                  const changed = changeLabel(stop.change);
                  const tint = stop.tier ? TIER_COLOR[stop.tier as SalesTier] : undefined;
                  // EVERY stop while any edit saves: the plan view ignores a second edit until the
                  // first has reloaded, so a live button elsewhere would be a click that does nothing.
                  const busy = !!busyClientId;
                  const mark = COMPLETION_MARK[stop.completion?.evidence ?? 'none'];
                  return (
                    <div
                      key={stop.client_id}
                      data-usage-seller-uid={stop.client_id}
                      data-usage-section="plan"
                      className={[
                        'spc-card',
                        isDropped ? 'spc-card-dropped' : '',
                        mark ? mark.cls : '',
                        selectedClientId === stop.client_id ? 'spc-card-selected' : '',
                      ].filter(Boolean).join(' ')}
                      onMouseEnter={() => onHover?.(stop.client_id)}
                      onMouseLeave={() => onHover?.(null)}
                    >
                      <div className="spc-card-main">
                        <button
                          className="spc-name"
                          onClick={() => onSelect?.(stop.client_id)}
                        >
                          {!isCalls ? <span className="spc-stopno">{stop.stop_no}</span> : null}
                          {mark ? (
                            <span className="spc-mark" title={mark.label}>{mark.glyph}</span>
                          ) : null}
                          {stop.client_name ?? stop.client_id}
                          {stop.is_anchor ? <span className="spc-anchor">ANCHOR</span> : null}
                        </button>

                        <div className="spc-meta">
                          <span className="spc-visit-type">{stop.visit_type}</span>
                          <span className="spc-where">{formatLocation(stop)}</span>
                          {!isCalls ? (() => {
                            const leg = formatLeg(stop);
                            return (
                              <span
                                className={
                                  leg.kind === 'unknown' ? 'spc-unknown'
                                    : leg.kind === 'straight' ? 'spc-straight' : undefined
                                }
                                title={
                                  leg.kind === 'straight'
                                    ? 'Straight-line distance: drive time is unavailable for this leg.'
                                    : leg.kind === 'drive' ? driveBasis : undefined
                                }
                              >
                                {leg.text}
                              </span>
                            );
                          })() : null}
                          {!isCalls && !isDropped && navStopUrl(stop, 'google') ? (
                            <TakeMeThere stop={stop} pref={navApp} onChoose={onChooseNavApp} />
                          ) : null}
                        </div>

                        {changed ? (
                          <span className={`spc-change spc-change-${stop.change}`}>{changed}</span>
                        ) : null}

                        {noteText(stop) ? (
                          <div className="spc-note">“{noteText(stop)}”</div>
                        ) : null}
                        {stop.reason_for_visit ? (
                          <div className="spc-focus">
                            <span className="spc-focus-label">
                              {isCalls ? 'Reason for call' : 'Reason for visit'}
                            </span>
                            {stop.reason_for_visit}
                          </div>
                        ) : !noteText(stop) && stop.reasons?.length ? (
                          <div className="spc-reasons">{stop.reasons.join(' · ')}</div>
                        ) : null}

                        {stop.return_reason ? (
                          <div className="spc-return">{stop.return_reason}</div>
                        ) : null}

                        {stop.account_mode ? (
                          <div className="spc-fixed">
                            Always a {stop.account_mode === 'CALL' ? 'call' : 'visit'}
                            {onSetAccountMode ? (
                              <button
                                className="spc-link"
                                aria-label={`Let the planner decide for ${stop.client_name ?? stop.client_id} again`}
                                title="Let the planner decide again from next week"
                                disabled={busy}
                                onClick={() => onSetAccountMode(stop.client_id, null)}
                              >
                                ✕
                              </button>
                            ) : null}
                          </div>
                        ) : null}

                        {/* Additive, never instead of the reasons: the rep is being sent
                            here by what is wrong, and arrives needing to know both.
                            Momentum leads; whitespace is the same words every Monday and
                            is ranked under it rather than beside it. */}
                        {/* Talking points carry the target, opportunities, pipeline, signals and
                            products in one ordered list. Weeks frozen before them fall back to
                            the separate lines below. */}
                        {stop.talking_points?.length ? (
                          <ul className="spc-points" aria-label="Talking points">
                            {stop.talking_points.map((p) => <li key={p}>{p}</li>)}
                          </ul>
                        ) : (
                          <>
                        {stop.target_reasons?.length ? (
                          <div className="spc-target" title="On a leader target list; lifted in the plan order.">
                            Target: {stop.target_reasons.join(' · ')}
                          </div>
                        ) : null}
                        {stop.opportunities?.length ? (
                          <div
                            className="spc-opps"
                            title="What is going right at this client. Never affects the plan order."
                          >
                            {stop.opportunities.join(' · ')}
                          </div>
                        ) : null}
                        {stop.whitespace?.length ? (
                          <div
                            className="spc-whitespace"
                            title="Products this client does not hold yet."
                          >
                            {stop.whitespace.join(' · ')}
                          </div>
                        ) : null}
                          </>
                        )}
                      </div>

                      <div className="spc-card-side">
                        {stop.tier ? (
                          <span
                            className="spc-tier"
                            style={tint ? { background: tint.bg, color: tint.fg } : undefined}
                          >
                            {stop.tier}
                          </span>
                        ) : null}

                        {onStatusChange ? (
                          <SalesStatusControl
                            client={{
                              client_id: stop.client_id,
                              user_status: (stop.user_status ?? null) as never,
                              user_status_by: stop.user_status_by ?? null,
                              user_status_at: stop.user_status_at ?? null,
                              user_status_reason_text: stop.user_status_reason_text ?? null,
                              user_status_follow_up_date: stop.user_status_follow_up_date ?? null,
                              user_status_expired: Boolean(stop.user_status_expired),
                            }}
                            onStatusChange={(id, status) => onStatusChange(id, status)}
                            variant="card"
                          />
                        ) : null}
                        <SalesforceLink salesforceId={stop.salesforce_id} />

                        <div className="spc-actions">
                          {!isDropped ? (
                            <button
                              className="spc-icon"
                              title={isCalls ? 'Make this a visit' : 'Make this a call'}
                              aria-label={
                                `Make ${stop.client_name ?? stop.client_id} a `
                                + `${isCalls ? 'visit' : 'call'}`
                              }
                              disabled={busy}
                              onClick={() => onEdit?.({
                                action: 'mode_changed',
                                client_id: stop.client_id,
                                contact_mode: isCalls ? 'VISIT' : 'CALL',
                              })}
                            >
                              {isCalls ? '⇢' : '☎'}
                            </button>
                          ) : null}
                          {!isDropped && onSetAccountMode && !stop.account_mode ? (
                            <button
                              className="spc-icon"
                              title={isCalls ? 'Always a visit, every week' : 'Always a call, every week'}
                              aria-label={
                                `Always make ${stop.client_name ?? stop.client_id} a `
                                + `${isCalls ? 'visit' : 'call'}`
                              }
                              disabled={busy}
                              onClick={() => onSetAccountMode(stop.client_id, isCalls ? 'VISIT' : 'CALL')}
                            >
                              📌
                            </button>
                          ) : null}
                          {!isCalls && !isDropped ? (
                            <>
                              <button
                                className="spc-icon"
                                title="Move up"
                                aria-label={`Move ${stop.client_name ?? stop.client_id} up`}
                                disabled={busy || index === 0}
                                onClick={() => onEdit?.({
                                  action: 'reordered',
                                  client_id: stop.client_id,
                                  to_stop: (stop.stop_no ?? 1) - 1,
                                })}
                              >
                                ↑
                              </button>
                              <button
                                className="spc-icon"
                                title="Move down"
                                aria-label={`Move ${stop.client_name ?? stop.client_id} down`}
                                disabled={busy || index === group.stops.length - 1}
                                onClick={() => onEdit?.({
                                  action: 'reordered',
                                  client_id: stop.client_id,
                                  to_stop: (stop.stop_no ?? 1) + 1,
                                })}
                              >
                                ↓
                              </button>
                              <button
                                className="spc-icon"
                                title="Start the trip here"
                                aria-label={`Start trip ${group.tripNo} at ${stop.client_name ?? stop.client_id}`}
                                disabled={busy || stop.is_anchor}
                                onClick={() => onEdit?.({
                                  action: 'anchored',
                                  client_id: stop.client_id,
                                })}
                              >
                                ⚑
                              </button>
                            </>
                          ) : null}
                          <button
                            className="spc-icon"
                            disabled={busy}
                            title={isDropped ? 'Put back' : 'Drop from the plan'}
                            aria-label={
                              `${isDropped ? 'Restore' : 'Drop'} ${stop.client_name ?? stop.client_id}`
                            }
                            onClick={() => onEdit?.({
                              action: isDropped ? 'added' : 'dropped',
                              client_id: stop.client_id,
                            })}
                          >
                            {isDropped ? '↺' : '✕'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {onFindNearby ? (
                  <SalesPlanAddNearby
                    tripNo={group.tripNo}
                    onFind={onFindNearby}
                    disabled={!!busyClientId}
                    onAdd={(clientId, tripNo) => onEdit?.({
                      action: 'added',
                      client_id: clientId,
                      contact_mode: tripNo === null ? 'CALL' : 'VISIT',
                      // Named, so it joins THIS trip rather than being re-placed by
                      // proximity into whichever one happens to be nearest.
                      ...(tripNo === null ? {} : { to_trip: tripNo }),
                    })}
                  />
                ) : null}
              </div>
            ) : null}
          </section>
        );
      })}
      {anyDrive && driveBasis ? <div className="spc-basis">{driveBasis}</div> : null}
    </div>
  );
};
